import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import WizardShell from './WizardShell';
import SourcePickStep from './steps/SourcePickStep';
import MediaPicker from './MediaPicker';
import { useStartCafeMatch } from '../../../queries/cafeWizard';
import { useCustomToast } from '../../Contexts/ToastContext';

/**
 * 🏟️ Match Mode / 🏟️🎵 Match + Music.
 *
 * mode='commentary' → Source            (HDMI-IN match with commentary audio)
 * mode='music'      → Source → Music     (match on screen, commentary muted, music playing)
 *
 * On finish it POSTs /cafe/wizard/match, which builds + activates a reusable
 * café Scene (a one-tap button in Remote Control) via the existing Scene engine.
 *
 * Props: mode:'commentary'|'music', onExit, onDone
 */
export default function MatchWizard({ mode = 'commentary', onExit, onDone }) {
  const { t } = useTranslation();
  const { showToast } = useCustomToast();
  const isMusic = mode === 'music';

  const [stepIndex, setStepIndex] = useState(0);
  const [receiverId, setReceiverId] = useState(null);
  const [channelId, setChannelId] = useState(null);
  const [musicIds, setMusicIds] = useState([]);

  const start = useStartCafeMatch();

  const meta = isMusic
    ? { icon: '🏟️🎵', title: t('cafe_wizard.match_music.title'), subtitle: t('cafe_wizard.match_music.subtitle'),
        steps: [t('cafe_wizard.step_source'), t('cafe_wizard.step_music')] }
    : { icon: '🏟️', title: t('cafe_wizard.match.title'), subtitle: t('cafe_wizard.match.subtitle'),
        steps: [t('cafe_wizard.step_source')] };

  const isLast = stepIndex === meta.steps.length - 1;

  const canNext = useMemo(() => {
    if (stepIndex === 0) return !!receiverId;
    return musicIds.length > 0; // music step (match+music only)
  }, [stepIndex, receiverId, musicIds]);

  const submit = () => {
    start.mutate(
      {
        name: meta.title,
        mode,
        receiver_id: receiverId,
        channel_id: channelId,
        music_media_ids: isMusic ? musicIds : [],
      },
      {
        onSuccess: (res) => {
          const ch = res?.data?.channel;
          showToast('success', meta.title,
            ch ? t('cafe_wizard.match_started_channel', { channel: ch }) : t('cafe_wizard.match_started'));
          onDone?.();
        },
      },
    );
  };

  const onBack = () => (stepIndex === 0 ? onExit?.() : setStepIndex((i) => i - 1));
  const onNext = () => (isLast ? submit() : setStepIndex((i) => i + 1));

  return (
    <WizardShell
      icon={meta.icon} title={meta.title} subtitle={meta.subtitle}
      steps={meta.steps} stepIndex={stepIndex}
      canNext={canNext} submitting={start.isLoading}
      onBack={onBack} onNext={onNext}
    >
      {stepIndex === 0 && (
        <SourcePickStep
          receiverId={receiverId} onReceiver={setReceiverId}
          channelId={channelId} onChannel={setChannelId}
        />
      )}
      {isMusic && stepIndex === 1 && (
        <MediaPicker family="music" value={musicIds} onChange={setMusicIds} allowUpload />
      )}
    </WizardShell>
  );
}
